import { Immutable } from "immer";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import iconCircleClose from "../../../../img/icons/circle-close.svg";
import {
    IAddress,
    IBillingAddress,
    IShippingAddress,
} from "../../../models/address";
import { getAddressParts } from "../../../utils/format";
import { focusElement } from "../../../utils/keyboardFocus";
import { IReduxFormState } from "../reducers.interfaces";

import styles from "./index.module.scss";

const modalStyle = {
    content: {
        top: "5%",
        bottom: "auto",
        left: "50%",
        right: "auto",
        width: "400px",
        padding: "30px",
        marginLeft: "-200px",
        overflow: "auto",
    },
};

const extractFormAddress = (
    form: IReduxFormState,
    addressPrefix: string,
): Partial<IAddress> => {
    return [
        "first_name",
        "last_name",
        "line1",
        "line2",
        "line4",
        "state",
        "postcode",
    ].reduce((address, field) => {
        const key = (addressPrefix + "_" + field) as keyof (
            | IShippingAddress
            | IBillingAddress
        );
        return { ...address, [field]: form[key] };
    }, {});
};

const handleModalAfterOpen = (): void => {
    focusElement(`.${styles.closeButton}`);
};

export const AddressSuggestionModal = (
    props: Immutable<{
        form: IReduxFormState;
        suggestedAddress: IAddress | null;
        onContinue: (shouldImport: boolean) => void;
        onRequestClose: () => void;
        onAfterClose: () => void;
        showAddressSuggestionModal: boolean;
        addressPrefix: string;
    }>,
): React.ReactNode => {
    const [acceptAddressSuggestion, setAcceptAddressSuggestion] =
        useState<boolean>(true);

    const formAddress = extractFormAddress(props.form, props.addressPrefix);

    const handleContinue = (): void => {
        props.onContinue(acceptAddressSuggestion);
    };

    const handleClose = (): void => {
        props.onRequestClose();
    };

    // Note: do NOT early-return before rendering <Modal> when
    // `suggestedAddress` is null. The Modal must always stay mounted so it
    // transitions open/closed through componentDidUpdate.
    const suggestedParts = props.suggestedAddress
        ? getAddressParts({
              line1: props.suggestedAddress.line1,
              line2: props.suggestedAddress.line2,
              city: props.suggestedAddress.line4,
              state: props.suggestedAddress.state,
              postal: props.suggestedAddress.postcode,
          })
        : null;

    const givenParts = getAddressParts({
        line1: formAddress.line1 || "",
        line2: formAddress.line2,
        city: formAddress.line4 || "",
        state: formAddress.state || "",
        postal: formAddress.postcode,
    });

    return (
        <Modal
            style={modalStyle}
            isOpen={
                props.showAddressSuggestionModal && !!props.suggestedAddress
            }
            onRequestClose={handleClose}
            onAfterClose={props.onAfterClose}
            role="dialog"
            aria={{
                labelledby: "address-verification",
                modal: true,
            }}
            onAfterOpen={handleModalAfterOpen}
        >
            {props.suggestedAddress && suggestedParts && (
                <>
                    <button
                        className={styles.closeButton}
                        aria-label={t`close`}
                        onClick={handleClose}
                    >
                        <SVG
                            aria-hidden="true"
                            className={styles.closeIcon}
                            src={iconCircleClose}
                            title={t`Close Icon`}
                        />
                    </button>
                    <div className={styles.root}>
                        <h1
                            className="ada-screenreader-only"
                            id="address-verification"
                        >
                            {t`Address Verification`}
                        </h1>
                        <h2 className={styles.header}>
                            {t`Please verify your address`}
                        </h2>
                        <fieldset className={styles.form}>
                            <legend>
                                <p>{t`SureAddress recommends using this address.`}</p>
                            </legend>
                            <label htmlFor="use_suggested_address">
                                <input
                                    type="radio"
                                    name="address_group"
                                    id="use_suggested_address"
                                    className={styles.radioInput}
                                    checked={acceptAddressSuggestion}
                                    onChange={() =>
                                        setAcceptAddressSuggestion(true)
                                    }
                                />{" "}
                                <span className={styles.radioTitle}>
                                    {t`Use the address suggested by SureAddress:`}
                                </span>
                                <p className="address-suggestion-model__address">
                                    <span className={styles.addressLine}>
                                        {props.suggestedAddress.first_name}{" "}
                                        {props.suggestedAddress.last_name}
                                    </span>
                                    <span className={styles.addressLine}>
                                        {suggestedParts.streetLine1}
                                    </span>
                                    {suggestedParts.streetLine2 && (
                                        <span className={styles.addressLine}>
                                            {suggestedParts.streetLine2}
                                        </span>
                                    )}
                                    <span className={styles.addressLine}>
                                        {suggestedParts.cityStatePostal}
                                    </span>
                                </p>
                            </label>
                            <label htmlFor="use_input_address">
                                <input
                                    type="radio"
                                    name="address_group"
                                    id="use_input_address"
                                    className={styles.radioInput}
                                    checked={!acceptAddressSuggestion}
                                    onChange={() =>
                                        setAcceptAddressSuggestion(false)
                                    }
                                />{" "}
                                <span className={styles.radioTitle}>
                                    {t`Use the address you entered:`}
                                </span>
                                <p className="address-suggestion-model__address">
                                    <span className={styles.addressLine}>
                                        {formAddress.first_name}{" "}
                                        {formAddress.last_name}
                                    </span>
                                    <span className={styles.addressLine}>
                                        {givenParts.streetLine1}
                                    </span>
                                    {givenParts.streetLine2 && (
                                        <span className={styles.addressLine}>
                                            {givenParts.streetLine2}
                                        </span>
                                    )}
                                    <span className={styles.addressLine}>
                                        {givenParts.cityStatePostal}
                                    </span>
                                </p>
                            </label>
                        </fieldset>
                        <button
                            className={`${styles.continueButton} button`}
                            onClick={handleContinue}
                        >
                            <span>{t`CONTINUE`}</span>
                        </button>
                    </div>
                </>
            )}
        </Modal>
    );
};
